import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { GroupModel, NO_ADMIN_ROLE } from '@/lib/models/group.model';
import {
  AdminPermissionLabels,
  AdminRoleLabels,
  AdminUserRole,
  CONVERSATION_ACCESS_PERMISSION,
} from '@/lib/models/admin-user.model';
import { Stack } from '@mui/material';
import { FC } from 'react';
import { getNiceNumber } from '@/lib/helpers';
import { StaticDashboard } from '@/components/metabase/StaticDashboard';
import useMetabase from '@/hooks/use-metabase';
import Chip from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';

interface Props {
  group: GroupModel;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Description', prop: 'description' },
  { label: 'Users', prop: 'userCount' },
  { label: 'Source', prop: 'source' },
  { label: 'Admin Role', prop: 'adminRole' },
  { label: 'Admin Permissions', prop: 'adminConversationAccess' },
  { label: 'Quick Insights', prop: 'quickInsights', propType: 'section' },
  { label: '', prop: 'insights' },
];

// ReadonlyView keeps spacing for a field with no data, so drop these on manual groups
const ADMIN_ACCESS_PROPS = new Set(['adminRole', 'adminConversationAccess']);

const getReadonlyData = (data: GroupModel | null, dashboardId: string) => {
  if (!data) {
    return {};
  }

  const {
    sourceLabel,
    userCount,
    name,
    description,
    hasDuplicateName,
    adminRole,
    adminConversationAccess,
  } = data;
  const NaChipEl = <NoneChip />;
  const hasAdminRole = adminRole !== NO_ADMIN_ROLE;

  return {
    name: (
      <Stack direction="row" gap={1}>
        {name}
        {hasDuplicateName && <Chip label="Duplicate Group Name" size="small" color="warning" />}
      </Stack>
    ),
    description: description || NaChipEl,
    source: sourceLabel,
    userCount: getNiceNumber(userCount),
    adminRole: hasAdminRole ? (AdminRoleLabels[adminRole as AdminUserRole] ?? adminRole) : NaChipEl,
    adminConversationAccess: adminConversationAccess ? (
      <Chip label={AdminPermissionLabels[CONVERSATION_ACCESS_PERMISSION]} size="small" />
    ) : (
      NaChipEl
    ),
    insights: (
      <StaticDashboard
        dashboardId={dashboardId}
        initialParameters={{ group: name }}
        hiddenParameters={['email', 'group']}
        isDrawer
      />
    ),
  };
};

export const GroupDetails: FC<Props> = ({ group }) => {
  const { dashboards } = useMetabase();
  const { id: dashboardId } = dashboards.userGroupSmall;

  const data = getReadonlyData(group, dashboardId);
  const visibleFields = group.isExternal
    ? fields
    : fields.filter(({ prop }) => !ADMIN_ACCESS_PROPS.has(prop));

  return <ReadonlyView fields={visibleFields} data={data} />;
};

export default GroupDetails;
